import React, { useEffect, useMemo, useState } from 'react';
import { StyledHome } from '@src/containers/VoiceCloning/Home/index.style';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import camelcaseKeys from 'camelcase-keys';
import {
  INSTANT_VOICE_CLONE,
  PRO_VOICE_CLONE,
  TIME_EXPIRE_QR_VOICE_CLONING,
  VOICE_CLONE_TYPE,
  VOICE_STATUS,
} from '@src/constants/voiceCloning';
import { useHistory, useLocation } from 'react-router-dom';
import { CRM_URL, LANDING_PAGE_URL } from '@src/configs';
import VoicesInformation from '@src/containers/VoiceCloning/Home/VoicesInformation';
import QRVoiceNewDialog from '@src/containers/VoiceCloning/QRDialog';
import DuplicateVoiceDialog from '@src/containers/VoiceCloning/Home/DuplicateVoiceDialog';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { useDispatch, useSelector } from 'react-redux';
import CrownImg from '@src/assets/images/crown.png';
import useCustomKeycloak from '@src/hooks/useCustomKeycloak';
import actions from '@src/redux/actions';
import {
  PACKAGE_DURATION,
  PACKAGE_LEVEL,
  PACKAGE_TYPE,
} from '@src/constants/package';
import moment from 'moment';
import useUserInfo from '@src/hooks/useTtsUser';
import {
  validateUserPackageCanUseVoiceCloning,
  validateUserCanUseInstantVoiceCloning,
} from '@src/services/voiceCloning';
import ROUTES from '@src/constants/route';
import NotificationDialogV4 from '@src/components/NotificationDialogV4';
import ExclamationMark from '@src/assets/images/warning.png';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import EmptyVoices from '../EmptyVoices';

const Voices = ({
  instantVoices = [],
  processingVoices = [],
  completedVoices = [],
  notUsedVoices = [],
  activeTab,
  handleTabChange,
  className,
  voices,
  totalProVoices = 0,
  setVoices,
  isSearch,
  handleOpenPopupInstantVoiceCloning,
  onContinueUploadVoice,
}) => {
  const { keycloak } = useCustomKeycloak();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [openQRVoiceNewDialog, setOpenQRVoiceNewDialog] = useState(false);
  const { voiceId: voiceIdParam } = camelcaseKeys(
    queryString.parse(location.search) || {},
  );

  const packages = useSelector((state) => state.packages.packages);

  const { handleGetPackageCodesByType } = useMappingPackage();

  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrExpireDate, setQrExpireDate] = useState('');
  const [qrRedirectUrl, setQRRedirectUrl] = useState('');
  const [
    showPopupLimitNumberVoiceCloning,
    setShowPopupLimitNumberVoiceCloning,
  ] = useState(false);
  const [showButtonUpgrade, setShowButtonUpgrade] = useState(false);
  const [redirectPaymentUrl, setRedirectPaymentUrl] = useState('');
  const [closeStates, setCloseStates] = useState({
    processing: false,
    completed: false,
    notUsed: false,
  });
  const [duplicatingVoice, setDuplicatingVoice] = useState(null);
  const [openDuplicateVoiceDialog, setOpenDuplicateVoiceDialog] =
    useState(false);

  const [packageVipYear, setPackageVipYear] = useState(null);

  const { usingPackage } = useSelector((state) => state.user);

  const { user } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();

  const { getFeatureValue } = useFeatureFlags();

  const handleCloseVoices = (type) => {
    setCloseStates((prevState) => ({
      ...prevState,
      [type]: !prevState[type],
    }));
  };

  const userInFeatureVoiceCloning = getFeatureValue(
    FEATURE_KEYS.VOICE_CLONING,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const checkUserCanUseVoiceCloning = validateUserPackageCanUseVoiceCloning({
    ttsUser,
    isUserInWhiteList: userInFeatureVoiceCloning,
  });

  const checkUserCanUseInstantVoiceCloning =
    validateUserCanUseInstantVoiceCloning({
      ttsUser,
    });

  const duplicateVoiceEnabled = getFeatureValue(FEATURE_KEYS.DUPLICATE_VOICE, {
    userId: user?.id,
    email: user?.email,
  });

  const hasVoiceRecording = voices.find(
    (item) =>
      item.status === VOICE_STATUS.RECORDING ||
      item.status === VOICE_STATUS.REJECTED,
  );

  const generateQRCodeUrl = async (loginCode) => {
    const screenApp = 'voiceCloning';
    return `${LANDING_PAGE_URL}/apps/aivoice-tts?screen=${screenApp}&loginCode=${loginCode}`;
  };

  const handleOpenQR = async (redirectUrl) => {
    setQRRedirectUrl(redirectUrl);

    const loginCode = await keycloak.generateLoginCode(redirectUrl);
    if (!loginCode) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: t('errorGenerateQRCode'),
        }),
      );
      return;
    }
    const url = await generateQRCodeUrl(loginCode);
    if (!url) return;

    setQrExpireDate(moment().add(TIME_EXPIRE_QR_VOICE_CLONING, 'milliseconds'));
    setQrCodeUrl(url);
  };

  const handleOpenQRVoiceRecording = (voiceRecordingId) => {
    const voice = voices.find((item) => item.id === voiceRecordingId);
    dispatch(actions.voiceVoiceCloning.addVoice(voice));
    const redirectUrl = `${CRM_URL}${ROUTES.VOICE_CLONING_VOICES}/${voiceRecordingId}`;
    if (checkUserCanUseVoiceCloning) handleOpenQR(redirectUrl);
    setOpenQRVoiceNewDialog(true);
  };

  const handleShowDialogUpgradePackage = () => {
    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('voiceCloningUpgrade'),
        img: CrownImg,
        classNameImg: 'custom-img',
        showButtonUnderstand: true,
        onUpgrade: () => {
          history.push(redirectPaymentUrl);
          dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
        },
      }),
    );

    return null;
  };

  const handleShowDialogUpgradeInstantVoiceCloning = () => {
    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('instantVoiceCloningUpgrade'),
        img: CrownImg,
        classNameImg: 'custom-img',
        showButtonUnderstand: true,
        onUpgrade: () => {
          history.push(redirectPaymentUrl);
          dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
        },
        maxWidth: 'sm',
      }),
    );

    return null;
  };

  const handleShowPopupLimitNumberVoiceCloning = (isVipPackage) => {
    setShowPopupLimitNumberVoiceCloning(true);
    setShowButtonUpgrade(!isVipPackage);
  };

  const handleContinueRecord = (voiceRecordingId) => {
    if (!checkUserCanUseVoiceCloning) {
      handleShowDialogUpgradePackage();
      return;
    }

    const hasRecording = !!hasVoiceRecording;
    if (hasRecording) {
      handleOpenQRVoiceRecording(voiceRecordingId);
    }
  };

  const isValidNumberOfVoicesCanUse = useMemo(() => {
    if (ttsUser && totalProVoices !== null && totalProVoices !== undefined) {
      const { maxCloningVoices = 0 } = ttsUser;
      if (totalProVoices >= maxCloningVoices) return false;
    }

    return true;
  }, [totalProVoices, ttsUser]);

  const handleValidateNumberClonedVoice = () => {
    const isVipPackage = usingPackage && usingPackage?.code === packageVipYear;
    handleShowPopupLimitNumberVoiceCloning(isVipPackage);
    return null;
  };

  const handleClickGenerateVoice = async () => {
    if (!checkUserCanUseVoiceCloning) {
      handleShowDialogUpgradePackage();
      return;
    }

    if (!isValidNumberOfVoicesCanUse) {
      handleValidateNumberClonedVoice();
      return;
    }

    const redirectUrl = `${CRM_URL}${ROUTES.VOICE_CLONING_CREATE}`;
    await handleOpenQR(redirectUrl);
    setOpenQRVoiceNewDialog(true);
  };

  const handleVoiceInfoChange = (id, newValues) => {
    const updatedVoices = voices.map((voice) =>
      voice.id === id ? { ...voice, ...newValues } : voice,
    );
    setVoices(updatedVoices);

    const updatedVoice = updatedVoices.find((voice) => voice.id === id);
    if (updatedVoice)
      dispatch(actions.voiceVoiceCloning.addVoice(updatedVoice));
  };

  const handleDeleteVoice = (id) => {
    const updatedVoices = voices.filter((voice) => voice.id !== id);
    setVoices(updatedVoices);
  };

  const handleChangeVoiceData = (id, data) => {
    const updatedVoices = voices.map((voice) =>
      voice.id === id ? { ...voice, ...data } : voice,
    );
    setVoices(updatedVoices);
  };

  const handleGetPackages = async () => {
    const packageCodes = handleGetPackageCodesByType({
      type: PACKAGE_TYPE.STUDIO,
    });

    const packageSpecialYearCode = packageCodes.find(
      (item) =>
        (item.includes(PACKAGE_LEVEL.SPECIAL) ||
          item.includes(PACKAGE_LEVEL.ENTERPRISE)) &&
        item.includes(PACKAGE_DURATION.YEAR),
    );
    const packageVipYearCode = packageCodes.find(
      (item) =>
        item.includes(PACKAGE_LEVEL.VIP) &&
        item.includes(PACKAGE_DURATION.YEAR),
    );

    setPackageVipYear(packageVipYearCode);
    setRedirectPaymentUrl(
      `${ROUTES.PAYMENT}?package=${packageSpecialYearCode}`,
    );
  };

  const handleDuplicateVoice = (id) => {
    if (!duplicateVoiceEnabled) return;

    if (!checkUserCanUseVoiceCloning) {
      handleShowDialogUpgradePackage();
      return;
    }

    if (!isValidNumberOfVoicesCanUse) {
      handleValidateNumberClonedVoice();
      return;
    }

    const voice = voices.find((v) => v.id === id);
    if (!voice) return;

    setDuplicatingVoice(voice);
    setOpenDuplicateVoiceDialog(true);
  };

  useEffect(() => {
    handleGetPackages();
  }, [packages]);

  const renderActionComponent = () => {
    if (showButtonUpgrade) {
      return (
        <>
          <Button
            className="outlined-button"
            onClick={() => setShowPopupLimitNumberVoiceCloning(false)}
          >
            {t('gotItV2')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              history.push(redirectPaymentUrl);
              setShowPopupLimitNumberVoiceCloning(false);
            }}
          >
            {t('upgradeNow')}
          </Button>
        </>
      );
    }

    return (
      <Button
        variant="contained"
        onClick={() => setShowPopupLimitNumberVoiceCloning(false)}
      >
        {t('gotItV2')}
      </Button>
    );
  };

  useEffect(() => {
    if (voices.length && voiceIdParam) {
      const voice = voices.find((v) => v.id === voiceIdParam);
      if (voice) {
        const tab =
          voice.type === VOICE_CLONE_TYPE.ZERO_SHOT
            ? INSTANT_VOICE_CLONE
            : PRO_VOICE_CLONE;
        handleTabChange(tab);
      }
    }
  }, [voices, voiceIdParam]);

  return (
    <>
      <StyledHome className={className}>
        {activeTab === INSTANT_VOICE_CLONE &&
          (instantVoices.length ? (
            <Box className="instant-voice-cloning">
              <VoicesInformation
                voices={instantVoices}
                closeVoices={false}
                checkUserCanUseVoiceCloning={checkUserCanUseInstantVoiceCloning}
                isShowTitle={false}
                isInstantVoiceCloning
                onDeleteVoice={handleDeleteVoice}
                handleChangeVoiceData={handleChangeVoiceData}
                onShowUpgradePackage={
                  handleShowDialogUpgradeInstantVoiceCloning
                }
              />
            </Box>
          ) : (
            <EmptyVoices
              description="emptyVoiceCloningInstant"
              isDescription={!isSearch}
              isCreate
              onCreate={handleOpenPopupInstantVoiceCloning}
            />
          ))}

        {activeTab === PRO_VOICE_CLONE &&
          (totalProVoices ? (
            <Box className="pro-voice-cloning">
              {!!processingVoices.length && (
                <VoicesInformation
                  boxVoicesName={t('process')}
                  onContinueUploadVoice={onContinueUploadVoice}
                  voices={processingVoices}
                  closeVoices={closeStates.processing}
                  onCloseVoices={() => handleCloseVoices('processing')}
                  checkUserCanUseVoiceCloning={checkUserCanUseVoiceCloning}
                  onContinueRecord={handleContinueRecord}
                  onVoiceInfoChange={handleVoiceInfoChange}
                  onDeleteVoice={handleDeleteVoice}
                  handleChangeVoiceData={handleChangeVoiceData}
                  handleClickGenerateVoice={handleClickGenerateVoice}
                  onShowUpgradePackage={handleShowDialogUpgradePackage}
                  onDuplicateVoice={handleDuplicateVoice}
                  onShowPopupLimitNumberVoiceCloning={
                    handleValidateNumberClonedVoice
                  }
                  isInstantVoiceCloning={false}
                />
              )}
              {!!completedVoices?.length && (
                <VoicesInformation
                  boxVoicesName={t('available')}
                  voices={completedVoices}
                  closeVoices={closeStates.completed}
                  onCloseVoices={() => handleCloseVoices('completed')}
                  checkUserCanUseVoiceCloning={checkUserCanUseVoiceCloning}
                  onContinueRecord={handleContinueRecord}
                  onVoiceInfoChange={handleVoiceInfoChange}
                  onDeleteVoice={handleDeleteVoice}
                  handleChangeVoiceData={handleChangeVoiceData}
                  handleClickGenerateVoice={handleClickGenerateVoice}
                  onShowUpgradePackage={handleShowDialogUpgradePackage}
                  onDuplicateVoice={handleDuplicateVoice}
                  onShowPopupLimitNumberVoiceCloning={
                    handleValidateNumberClonedVoice
                  }
                  isInstantVoiceCloning={false}
                />
              )}
              {!!notUsedVoices?.length && (
                <VoicesInformation
                  boxVoicesName={t('unavailable')}
                  voices={notUsedVoices}
                  closeVoices={closeStates.notUsed}
                  onCloseVoices={() => handleCloseVoices('notUsed')}
                  checkUserCanUseVoiceCloning={checkUserCanUseVoiceCloning}
                  onContinueRecord={handleContinueRecord}
                  onVoiceInfoChange={handleVoiceInfoChange}
                  onDeleteVoice={handleDeleteVoice}
                  handleChangeVoiceData={handleChangeVoiceData}
                  handleClickGenerateVoice={handleClickGenerateVoice}
                  onShowUpgradePackage={handleShowDialogUpgradePackage}
                  onDuplicateVoice={handleDuplicateVoice}
                  onShowPopupLimitNumberVoiceCloning={
                    handleValidateNumberClonedVoice
                  }
                  isInstantVoiceCloning={false}
                />
              )}
            </Box>
          ) : (
            <EmptyVoices
              description="emptyVoiceCloningPro"
              isDescription={!isSearch}
              createText={t('createProVoice')}
              isCreate
              onCreate={handleClickGenerateVoice}
            />
          ))}

        <QRVoiceNewDialog
          urlToShow={qrCodeUrl}
          open={openQRVoiceNewDialog}
          qrExpireDate={qrExpireDate}
          qrRedirectUrl={qrRedirectUrl}
          onClose={() => setOpenQRVoiceNewDialog(false)}
          refreshQRCode={handleOpenQR}
        />
      </StyledHome>
      {showPopupLimitNumberVoiceCloning && (
        <NotificationDialogV4
          image={showButtonUpgrade ? CrownImg : ExclamationMark}
          title={t('voiceCloningLimitTitle')}
          description={
            showButtonUpgrade
              ? t('voiceCloningLimitDescWithUpgrade', {
                  numberVoices: ttsUser?.maxCloningVoices || 0,
                })
              : t('voiceCloningLimitDescWithNoUpgrade', {
                  numberVoices: ttsUser?.maxCloningVoices || 0,
                })
          }
          actionComponent={renderActionComponent()}
          open={showPopupLimitNumberVoiceCloning}
          onClose={() => setShowPopupLimitNumberVoiceCloning(false)}
        />
      )}
      {openDuplicateVoiceDialog && (
        <DuplicateVoiceDialog
          open={openDuplicateVoiceDialog}
          onClose={() => setOpenDuplicateVoiceDialog(false)}
          duplicatingVoice={duplicatingVoice}
        />
      )}
    </>
  );
};

export default Voices;
